import React, { useState, useEffect } from 'react';

const BOARD_SIZE = 100;

// Helper function to generate random snakes and ladders
function generateSnakesAndLadders(numLadders = 5, numSnakes = 5) {
  const boardSize = BOARD_SIZE;
  const usedPositions = new Set();
  const ladders = {};
  const snakes = {};

  function getRandomPos() {
    return Math.floor(Math.random() * (boardSize - 2)) + 2; // positions 2 to 99
  }

  // Generate ladders: start < end
  for (let i = 0; i < numLadders; i++) {
    let start, end;
    do {
      start = getRandomPos();
      end = getRandomPos();
    } while (
      start >= end ||
      usedPositions.has(start) ||
      usedPositions.has(end) ||
      start === 1 ||
      end === boardSize
    );
    ladders[start] = end;
    usedPositions.add(start);
    usedPositions.add(end);
  }

  // Generate snakes: start > end
  for (let i = 0; i < numSnakes; i++) {
    let start, end;
    do {
      start = getRandomPos();
      end = getRandomPos();
    } while (
      start <= end ||
      usedPositions.has(start) ||
      usedPositions.has(end) ||
      start === 1 ||
      end === boardSize
    );
    snakes[start] = end;
    usedPositions.add(start);
    usedPositions.add(end);
  }

  return { ...ladders, ...snakes };
}

// Dice component with rolling animation
function Dice({ value, rolling }) {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (rolling) {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [rolling]);

  const Dot = ({ visible }) => (
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: 'black',
        visibility: visible ? 'visible' : 'hidden',
      }}
    />
  );

  const diceDotMap = {
    1: [4],
    2: [0, 8],
    3: [0, 4, 8],
    4: [0, 2, 6, 8],
    5: [0, 2, 4, 6, 8],
    6: [0, 2, 3, 5, 6, 8],
  };

  const dotsToShow = diceDotMap[value] || [];

  return (
    <div
      className={animate ? 'dice rolling' : 'dice'}
      style={{
        width: 60,
        height: 60,
        backgroundColor: '#f0f0f0',
        borderRadius: 12,
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'repeat(3, 1fr)',
        gap: 6,
        justifyItems: 'center',
        alignItems: 'center',
        userSelect: 'none',
        margin: 'auto',
      }}
    >
      {[...Array(9)].map((_, i) => (
        <Dot key={i} visible={dotsToShow.includes(i)} />
      ))}

      <style>{`
        .dice.rolling {
          animation: shake 0.8s ease-in-out;
        }

        @keyframes shake {
          0% { transform: rotate(0deg); }
          20% { transform: rotate(15deg); }
          40% { transform: rotate(-15deg); }
          60% { transform: rotate(10deg); }
          80% { transform: rotate(-10deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>
    </div>
  );
}

// Main game component
function SnakeAndLadder() {
  const [snakesAndLadders, setSnakesAndLadders] = useState({});
  const [playerPos, setPlayerPos] = useState(1);
  const [computerPos, setComputerPos] = useState(1);
  const [turn, setTurn] = useState('player'); // 'player' or 'computer'
  const [dice, setDice] = useState(null);
  const [message, setMessage] = useState('Your turn! Roll the dice.');
  const [gameOver, setGameOver] = useState(false);
  const [rolling, setRolling] = useState(false);

  // Generate snakes and ladders once when component mounts
  useEffect(() => {
    const generated = generateSnakesAndLadders(5, 5);
    setSnakesAndLadders(generated);
  }, []);

  // Move logic with snakes and ladders check
  const movePlayer = (currentPos, roll) => {
    let newPos = currentPos + roll;
    if (newPos > BOARD_SIZE) newPos = currentPos;
    if (snakesAndLadders[newPos]) {
      newPos = snakesAndLadders[newPos];
    }
    return newPos;
  };

  // Player roll dice handler
  const rollDice = () => {
    if (gameOver || rolling || turn !== 'player') return;

    setRolling(true);
    setMessage('Rolling dice...');

    setTimeout(() => {
      const roll = Math.floor(Math.random() * 6) + 1;
      setDice(roll);

      const newPos = movePlayer(playerPos, roll);
      setPlayerPos(newPos);
      setMessage(`You rolled a ${roll} and moved to ${newPos}.`);

      if (newPos === BOARD_SIZE) {
        setMessage('🎉 You won! 🎉');
        setGameOver(true);
        setRolling(false);
        return;
      }

      setTurn('computer');
      setRolling(false);

      setTimeout(() => computerMove(), 1500);
    }, 1000);
  };

  // Computer move logic
  const computerMove = () => {
    if (gameOver) return;

    setRolling(true);
    setMessage('Computer rolling dice...');

    setTimeout(() => {
      const roll = Math.floor(Math.random() * 6) + 1;
      setDice(roll);

      const newPos = movePlayer(computerPos, roll);
      setComputerPos(newPos);
      setMessage(`Computer rolled a ${roll} and moved to ${newPos}.`);

      if (newPos === BOARD_SIZE) {
        setMessage('💻 Computer won! 💻');
        setGameOver(true);
        setRolling(false);
        return;
      }

      setTurn('player');
      setMessage('Your turn! Roll the dice.');
      setRolling(false);
    }, 1000);
  };

  return (
    <div style={{ maxWidth: 450, margin: '20px auto', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
      <h2>Snake and Ladder</h2>
      <p style={{ minHeight: 24 }}>{message}</p>
      <Dice value={dice || 1} rolling={rolling} />
      <p>
        <strong>Your position:</strong> {playerPos} &nbsp;&nbsp;&nbsp; <strong>Computer position:</strong> {computerPos}
      </p>
      <button
        onClick={rollDice}
        disabled={turn !== 'player' || rolling || gameOver}
        style={{ padding: '8px 16px', fontSize: 16, cursor: turn === 'player' && !rolling && !gameOver ? 'pointer' : 'not-allowed' }}
      >
        Roll Dice
      </button>

      <div style={{ marginTop: 20 }}>
        <Board playerPos={playerPos} computerPos={computerPos} snakesAndLadders={snakesAndLadders} />
      </div>
    </div>
  );
}

// Board component displays the grid with player tokens and snakes/ladders colors
function Board({ playerPos, computerPos, snakesAndLadders }) {
  const squares = [];
  for (let i = BOARD_SIZE; i >= 1; i--) {
    squares.push(i);
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(10, 30px)',
        gap: 2,
        border: '2px solid #333',
        padding: 5,
        userSelect: 'none',
        position: 'relative',
        margin: 'auto',
        width: 310,
      }}
    >
      {squares.map((num) => {
        const isPlayerHere = playerPos === num;
        const isComputerHere = computerPos === num;

        let bgColor = '#eee';
        if (snakesAndLadders[num] && num < snakesAndLadders[num]) {
          bgColor = '#a3dca6'; // ladder start - greenish
        } else if (snakesAndLadders[num] && num > snakesAndLadders[num]) {
          bgColor = '#f2a6a6'; // snake head - reddish
        }

        return (
          <div
            key={num}
            style={{
              width: 30,
              height: 30,
              backgroundColor: bgColor,
              border: '1px solid #999',
              fontSize: 10,
              textAlign: 'center',
              position: 'relative',
              userSelect: 'none',
            }}
            title={`Square ${num}`}
          >
            {num}
            {isPlayerHere && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 2,
                  left: 6,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: 'blue',
                  border: '0 solid white',
                }}
                title="Player 1"
              />
            )}
            {isComputerHere && (
              <div
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 6,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: 'red',
                  border: '0 solid white',
                }}
                title="Player 2"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default SnakeAndLadder;
